import { useState, useEffect } from "react";
import { useSignal } from "@preact/signals-react";
import { toast } from "react-toastify";
import CurrencyInput from "../../../../molecules/CurrencyInput";
import TextEdit from "../../../../molecules/TextEdit";
import SelectTextField from "../../../../molecules/SelectTextField";
import { applyDateMask } from "../../../../../utils/dateMask";
import { InstallmentItem } from "../../types/operations";
import { isValidDate, formatDateWithoutTimezone } from "../../utils/helpers/operationHelpers";

interface Props {
    installments: InstallmentItem[];
    onAdd: (installment: InstallmentItem) => void;
    onRemove: (index: number) => void;
    onUpdateItem: (index: number, installment: InstallmentItem) => void;
    onUpdate?: (installments: InstallmentItem[], lastDueDate?: string) => void;
    disabled?: boolean;
}

const formatPercentual = (value: number): string =>
    `${(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}%`;

const formatCurrency = (value: number): string =>
    `R$ ${(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatInstallmentValue = (format: string, value: number): string =>
    format === 'PERCENTUAL' ? formatPercentual(value) : formatCurrency(value);

const InstallmentManager: React.FC<Props> = ({ installments, onAdd, onRemove, onUpdateItem, onUpdate, disabled = false }) => {
    const newInstallment = useSignal<{ number: number; value: number; dueDate: string | null; format: string }>({
        number: 1,
        value: 0,
        dueDate: new Date().toLocaleDateString('pt-BR'),
        format: 'VALOR'
    });
    const editingIndex = useSignal<number | null>(null);
    const editingInstallment = useSignal<InstallmentItem>({ number: 1, value: 0, dueDate: '', format: 'VALOR' });

    const reprocessNumbers = (items: InstallmentItem[]): InstallmentItem[] => {
        const sorted = [...items].sort((a, b) => {
            const dateA = new Date(a.dueDate.split('/').reverse().join('-'));
            const dateB = new Date(b.dueDate.split('/').reverse().join('-'));
            return dateA.getTime() - dateB.getTime();
        });
        return sorted.map((item, index) => ({ ...item, number: index + 1 }));
    };

    const getLastDueDate = (items: InstallmentItem[]): string | undefined => {
        if (items.length === 0) return undefined;
        const sorted = [...items].sort((a, b) => {
            const dateA = new Date(a.dueDate.split('/').reverse().join('-'));
            const dateB = new Date(b.dueDate.split('/').reverse().join('-'));
            return dateB.getTime() - dateA.getTime();
        });
        return sorted[0].dueDate;
    };

    const handleAdd = () => {
        if (newInstallment.value.value <= 0) {
            toast.error("Informe um valor maior que zero");
            return;
        }
        if (!newInstallment.value.dueDate) {
            toast.error("Informe a data de vencimento");
            return;
        }

        const newItem: InstallmentItem = {
            number: 1,
            value: newInstallment.value.value,
            dueDate: newInstallment.value.dueDate,
            format: newInstallment.value.format,
            business_reference: 'INSTALLMENT'
        };

        const updated = reprocessNumbers([...installments, newItem]);
        const lastDueDate = getLastDueDate(updated);
        onAdd(newItem);
        if (onUpdate) onUpdate(updated, lastDueDate);

        newInstallment.value = {
            number: 1,
            value: 0,
            dueDate: new Date().toLocaleDateString('pt-BR'),
            format: 'VALOR'
        };
    };

    const handleRemove = (index: number) => {
        const filtered = installments.filter((_, i) => i !== index);
        const updated = reprocessNumbers(filtered);
        const lastDueDate = getLastDueDate(updated);
        onRemove(index);
        if (onUpdate) onUpdate(updated, lastDueDate);
    };

    const handleEdit = (index: number) => {
        editingIndex.value = index;
        editingInstallment.value = { ...installments[index] };
    };

    const handleSave = (index: number) => {
        if (editingInstallment.value.value <= 0) {
            toast.error("Informe um valor maior que zero");
            return;
        }
        if (!isValidDate(editingInstallment.value.dueDate)) {
            toast.error("Data de vencimento inválida");
            return;
        }
        onUpdateItem(index, editingInstallment.value);
        editingIndex.value = null;
    };

    const handleCancel = () => {
        editingIndex.value = null;
    };

    const sortedInstallments = [...installments].sort((a, b) => a.number - b.number);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 25;
    const totalPages = Math.ceil(sortedInstallments.length / itemsPerPage);
    const paginatedInstallments = sortedInstallments.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);


    const hasDocumentInfo = installments.some(i => i.document_id || i.document_title || i.receivable_installment);
    const hasDebtorInfo = installments.some(i => i.debtor_name || i.debtor_cpf_cnpj);
    const hasAddressInfo = installments.some(i => i.debtor_address_street || i.debtor_address_city);

    return (
        <div>
            <h2 className="text-xl font-semibold mt-4 mb-4">Parcelas</h2>

            <div className="grid grid-cols-12 gap-4 p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                <div className="col-span-2">
                    {newInstallment.value.format === 'PERCENTUAL' ? (
                        <CurrencyInput
                            id="installment_percentage"
                            label="Percentual da Parcela"
                            prefix="%"
                            fractionDigits={2}
                            value={newInstallment.value.value}
                            onChange={(value) => {
                                const clampedValue = Math.min(Math.max(value, 0), 100);
                                newInstallment.value = { ...newInstallment.value, value: clampedValue };
                            }}
                        />
                    ) : (
                        <CurrencyInput
                            id="installment_value"
                            label="Valor da Parcela"
                            prefix="R$"
                            fractionDigits={2}
                            value={newInstallment.value.value}
                            onChange={(value) => {
                                newInstallment.value = { ...newInstallment.value, value: value };
                            }}
                        />
                    )}
                </div>
                <div className="col-span-2">
                    <TextEdit
                        id="installment_due_date"
                        label="Data de Vencimento"
                        value={newInstallment.value.dueDate || ''}
                        onChange={(e) => {
                            newInstallment.value = { ...newInstallment.value, dueDate: applyDateMask(e.target.value) };
                        }}
                        onBlur={(e) => {
                            if (!isValidDate(e.target.value)) {
                                toast.error('Data de Vencimento inválida');
                                e.target.focus();
                            }
                        }}
                        placeholder="dd/mm/aaaa"
                    />
                </div>
                <div className="col-span-2">
                    <SelectTextField
                        id="format"
                        label="Formato"
                        value={newInstallment.value.format}
                        items={[
                            { value: 'VALOR', label: 'Valor' },
                            { value: 'PERCENTUAL', label: 'Percentual' }
                        ]}
                        onChange={(e) => {
                            newInstallment.value = { ...newInstallment.value, format: e.target.value };
                        }}
                    />
                </div>
                <div className="col-span-2 flex items-end">
                    <button type="button"
                        onClick={handleAdd}
                        className="btn-add mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={disabled}
                    >
                        Incluir
                    </button>
                </div>
            </div>

            <div className="mt-4 overflow-x-auto">
                <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600" aria-label="Parcelas">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Parcela</th>
                            {hasDocumentInfo && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">NFe</th>}
                            {hasDocumentInfo && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Título</th>}
                            {hasDocumentInfo && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Parcela Recebível</th>}
                            {hasDebtorInfo && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Devedor</th>}
                            {hasDebtorInfo && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">CPF/CNPJ Devedor</th>}
                            {hasAddressInfo && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Endereço</th>}
                            {hasAddressInfo && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Cidade/UF</th>}
                            {hasAddressInfo && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">CEP</th>}
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Valor</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Vencimento</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Formato</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                        {installments.length === 0 ? (
                            <tr>
                                <td colSpan={5 + (hasDocumentInfo ? 3 : 0) + (hasDebtorInfo ? 2 : 0) + (hasAddressInfo ? 3 : 0)} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                    Nenhuma parcela adicionada
                                </td>
                            </tr>
                        ) : (
                            paginatedInstallments.map((installment, index) => {
                                const originalIndex = installments.findIndex(inst => inst.number === installment.number);
                                const isEditing = editingIndex.value === originalIndex;
                                return (
                                <tr key={`${index}-${installment.number}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100 font-medium">
                                        {installment.number}
                                    </td>
                                    {hasDocumentInfo && <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                        {installment.document_id || '-'}
                                    </td>}
                                    {hasDocumentInfo && <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                        {installment.document_title || '-'}
                                    </td>}
                                    {hasDocumentInfo && <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                        {installment.receivable_installment || '-'}
                                    </td>}
                                    {hasDebtorInfo && <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                        {installment.debtor_name || '-'}
                                    </td>}
                                    {hasDebtorInfo && <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                        {installment.debtor_cpf_cnpj || '-'}
                                    </td>}
                                    {hasAddressInfo && <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100" title={[installment.debtor_address_street, installment.debtor_address_number, installment.debtor_address_complement].filter(Boolean).join(', ')}>
                                        {[installment.debtor_address_street, installment.debtor_address_number].filter(Boolean).join(', ') || '-'}
                                    </td>}
                                    {hasAddressInfo && <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                        {[installment.debtor_address_city, installment.debtor_address_state].filter(Boolean).join('/') || '-'}
                                    </td>}
                                    {hasAddressInfo && <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                        {installment.debtor_address_zip_code || '-'}
                                    </td>}
                                    <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100 font-medium">
                                        {isEditing ? (
                                            <CurrencyInput
                                                id={`edit_installment_value_${originalIndex}`}
                                                label=""
                                                prefix={editingInstallment.value.format === 'PERCENTUAL' ? '' : 'R$'}
                                                fractionDigits={editingInstallment.value.format === 'PERCENTUAL' ? 6 : 2}
                                                value={editingInstallment.value.value}
                                                onChange={(value) => {
                                                    editingInstallment.value = { ...editingInstallment.value, value };
                                                }}
                                            />
                                        ) : (
                                            formatInstallmentValue(installment.format, installment.value)
                                        )}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                        {isEditing ? (
                                            <TextEdit
                                                id={`edit_installment_date_${originalIndex}`}
                                                label=""
                                                value={editingInstallment.value.dueDate}
                                                onChange={(e) => {
                                                    editingInstallment.value = { ...editingInstallment.value, dueDate: applyDateMask(e.target.value) };
                                                }}
                                                placeholder="dd/mm/aaaa"
                                            />
                                        ) : (
                                            formatDateWithoutTimezone(installment.dueDate)
                                        )}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                        {isEditing ? (
                                            <SelectTextField
                                                id={`edit_installment_format_${originalIndex}`}
                                                label=""
                                                value={editingInstallment.value.format}
                                                items={[
                                                    { value: 'VALOR', label: 'VALOR' },
                                                    { value: 'PERCENTUAL', label: 'PERCENTUAL' }
                                                ]}
                                                onChange={(e) => {
                                                    editingInstallment.value = { ...editingInstallment.value, format: e.target.value };
                                                }}
                                            />
                                        ) : (
                                            installment.format || 'VALOR'
                                        )}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap">
                                        {isEditing ? (
                                            <div className="flex gap-2">
                                                <button type="button"
                                                    onClick={() => handleSave(originalIndex)}
                                                    className="bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600"
                                                >
                                                    Salvar
                                                </button>
                                                <button type="button"
                                                    onClick={handleCancel}
                                                    className="bg-gray-500 text-white px-2 py-1 rounded text-xs hover:bg-gray-600"
                                                >
                                                    Cancelar
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex gap-2">
                                                <button type="button"
                                                    onClick={() => handleEdit(originalIndex)}
                                                    className="bg-blue-500 text-white px-2 py-1 rounded text-xs hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    disabled={disabled}
                                                >
                                                    Editar
                                                </button>
                                                <button type="button"
                                                    onClick={() => handleRemove(originalIndex)}
                                                    className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    disabled={disabled}
                                                >
                                                    Remover
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            )})
                        )}
                    </tbody>
                </table>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 border border-t-0 border-gray-300 dark:border-gray-600 rounded-b px-2 py-2">
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Total de parcelas: {installments.length}
                </div>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">
                    Soma das parcelas: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(installments.reduce((sum, i) => sum + i.value, 0))}
                </div>
                {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-2">
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                            Mostrando {(currentPage - 1) * itemsPerPage + 1} a {Math.min(currentPage * itemsPerPage, sortedInstallments.length)} de {sortedInstallments.length} parcelas
                        </div>
                        <div className="flex gap-2">
                            <button type="button"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-600"
                            >
                                Anterior
                            </button>
                            <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
                                Página {currentPage} de {totalPages}
                            </span>
                            <button type="button"
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-600"
                            >
                                Próxima
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InstallmentManager;
